<script setup lang="ts">
import { RouterLink } from 'vue-router'

import { useActiveNavItem } from '@/composables/navigation/use-active-nav-item.js'
import type { NavItem } from '@/constants/app/navigation.constant.js'

/**
 * The section nav, as a scrolling strip of pills under the header on a phone.
 *
 * A 248 px column does not fit beside content on a 390 px screen, and burying a section's
 * screens in the sheet would put "Default promotions" two taps from "Shop promotions". A
 * strip keeps them one tap apart and visible. Not rendered for a one-screen section: a
 * strip with one pill is a heading pretending to be a control.
 */
defineProps<{ label: string; items: readonly NavItem[] }>()

const active = useActiveNavItem()
</script>

<template>
  <nav
    v-if="items.length > 1"
    class="flex shrink-0 gap-1.5 overflow-x-auto border-b border-border-subtle bg-raised px-4 py-2 lg:hidden"
    :aria-label="label"
  >
    <template v-for="item in items" :key="item.label">
      <RouterLink
        v-if="item.available && item.route !== null"
        :to="{ name: item.route, query: item.query ?? {} }"
        :class="[
          'shrink-0 rounded-full border px-3 py-1.5 text-caption font-medium whitespace-nowrap transition-colors duration-(--motion-fast)',
          active(item) ? 'border-action-tint-border bg-action-tint text-action' : 'border-border-subtle text-ink-secondary hover:text-ink',
        ]"
        :aria-current="active(item) ? 'page' : undefined"
      >
        {{ item.label }}
      </RouterLink>
      <span
        v-else
        class="shrink-0 cursor-not-allowed rounded-full border border-border-hairline px-3 py-1.5 text-caption text-ink-disabled whitespace-nowrap"
        :aria-disabled="true"
      >
        {{ item.label }}
      </span>
    </template>
  </nav>
</template>
